import { getApiBaseUrl } from "./config";
import { CSRF_HEADER_NAME, captureCsrfToken, getCsrfToken } from "./csrf";

/**
 * POST /auth/token/refresh — confirmed in src/server/routes/token.ts, mounted
 * at /auth/token. Exempt from CSRF enforcement (EXEMPT_PREFIXES includes
 * '/auth/token/refresh' in src/server/middleware/csrf.ts), but we still send
 * the header when we have one — harmless, and covers the CSRF-rotation retry
 * below.
 */
const REFRESH_PATH = "/auth/token/refresh";

/**
 * What an attempt established about the session.
 *
 * `unavailable` is the one that must not sign anyone out: a 429 from the
 * per-user refresh limiter, a 502 while the API redeploys or a tab that woke
 * before its network did all leave a 30-day refresh token perfectly valid.
 * Only the refresh endpoint refusing the token says the session is gone.
 */
export type RefreshOutcome = "renewed" | "expired" | "unavailable";

/** 400 is the answer when the refresh cookie is absent entirely. */
const SESSION_GONE_STATUSES = new Set([400, 401]);

let refreshPromise: Promise<RefreshOutcome> | null = null;

async function performRefresh(): Promise<RefreshOutcome> {
  try {
    const csrfToken = getCsrfToken();
    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;

    const response = await fetch(`${getApiBaseUrl()}${REFRESH_PATH}`, {
      method: "POST",
      credentials: "include",
      headers,
    });

    // setAuthCookies() mints a fresh CSRF token on every refresh, so the one
    // held from before this call is now stale and the retried request that
    // prompted the refresh would 403 with it. Body parsing stays best-effort:
    // a refresh that succeeded must not be reported as failed.
    if (response.ok) {
      captureCsrfToken(REFRESH_PATH, await response.json().catch(() => null));
      return "renewed";
    }

    return SESSION_GONE_STATUSES.has(response.status)
      ? "expired"
      : "unavailable";
  } catch {
    return "unavailable";
  }
}

/**
 * The single-flight above coalesces refreshes within one tab, but it is module
 * state so each tab has its own. Two tabs that 401 at the same moment would
 * each refresh with the shared cookie; the first rotates the refresh token and
 * the server refuses the second as a reused token, 401s, and clears the shared
 * cookie jar, signing both tabs out while the session is in fact alive. A
 * same-origin Web Lock serializes the refresh across tabs, so a later tab runs
 * only once the first has rotated the cookie and then refreshes the current
 * token instead of a revoked one.
 */
const REFRESH_LOCK_NAME = "autar-token-refresh";

async function withRefreshLock(
  run: () => Promise<RefreshOutcome>,
): Promise<RefreshOutcome> {
  if (typeof navigator === "undefined" || !("locks" in navigator)) {
    return run();
  }
  return await navigator.locks.request(REFRESH_LOCK_NAME, run);
}

/**
 * Refresh the access token exactly once even when several requests 401 at
 * the same time. Concurrent callers await the same in-flight promise instead
 * of each firing their own refresh request.
 */
export function refreshSession(): Promise<RefreshOutcome> {
  if (!refreshPromise) {
    refreshPromise = withRefreshLock(performRefresh).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Refresh for a 401 and report whether the request can be retried.
 *
 * The refusal is announced here rather than at the call site: every caller
 * that renews a session on a 401 owes the shell that event, and a caller that
 * forgets leaves a signed-in shell in front of a session the API has ended.
 * An unanswered refresh stays silent, since the session behind it is intact.
 */
export async function renewForRetry(): Promise<boolean> {
  return (await renewSession()) === "renewed";
}

/** {@link renewForRetry}, reporting the outcome itself. */
export async function renewSession(): Promise<RefreshOutcome> {
  const outcome = await refreshSession();
  if (outcome === "expired" && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:required"));
  }
  return outcome;
}

export function isRefreshPath(path: string): boolean {
  return path === REFRESH_PATH;
}
